import {
  Button,
  CloseButton,
  DatePicker,
  TextInput,
  TimePicker,
  TimePickerVariant,
} from '@consigli/facade';
import { useCreateFindingMutation } from '@consigli/hooks';
import { ConflictCategory, ConflictType, FindingStatus, ProcessorName } from '@consigli/types';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { buildMilestoneDateString } from './rfp-timeline-utils';

interface AddMilestoneDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  packageId: string;
}

interface FormData {
  milestone: string;
  date: string;
  time: string;
}

interface FormErrors {
  milestone?: string;
  date?: string;
}

export const AddMilestoneDialog: FC<AddMilestoneDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  packageId,
}) => {
  const { t } = useTranslation();
  const [createFinding, { isLoading }] = useCreateFindingMutation();
  const [formData, setFormData] = useState<FormData>({ milestone: '', date: '', time: '' });
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!formData.milestone.trim()) {
      next.milestone = t('dashboard.rfp-timeline.add-milestone.error-milestone-required');
    }
    if (!formData.date) {
      next.date = t('dashboard.rfp-timeline.add-milestone.error-date-required');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setFormData({ milestone: '', date: '', time: '' });
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const dateStr = buildMilestoneDateString(formData.date, formData.time || undefined);
      await createFinding({
        projectId,
        packageId,
        finding: {
          processor_name: ProcessorName.HUMAN_INTELLIGENCE,
          message_template: 'FT001',
          message_context: {
            CUSTOM_MESSAGE: { date: dateStr, milestone: formData.milestone.trim() },
          },
          message_context_en: {},
          status: FindingStatus.ACTIVE,
          conflict_message: formData.milestone.trim(),
          conflict_type: ConflictType.MILESTONE,
          conflict_category: ConflictCategory.INSIGHT,
          download_url: null,
        },
      }).unwrap();
      toast.success(t('dashboard.rfp-timeline.add-milestone.success'));
      handleClose();
    } catch {
      toast.error(t('dashboard.rfp-timeline.add-milestone.error'));
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
      role="button"
      tabIndex={0}
      aria-label={t('dashboard.rfp-timeline.add-milestone.close')}
    >
      <div
        className="w-full max-w-md bg-white rounded-lg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-milestone-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="add-milestone-title" className="text-lg font-semibold text-gray-900">
            {t('dashboard.rfp-timeline.add-milestone.title')}
          </h2>
          <CloseButton
            onClick={handleClose}
            ariaLabel={t('dashboard.rfp-timeline.add-milestone.close')}
          />
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <TextInput
              label={t('dashboard.rfp-timeline.add-milestone.label-milestone')}
              value={formData.milestone}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, milestone: e.target.value }));
                if (errors.milestone) setErrors((prev) => ({ ...prev, milestone: undefined }));
              }}
              placeholder={t('dashboard.rfp-timeline.add-milestone.placeholder-milestone')}
              disabled={isLoading}
              className="w-full"
            />
            {errors.milestone && <p className="text-red-500 text-xs mt-1">{errors.milestone}</p>}
          </div>

          <div>
            <DatePicker
              label={t('dashboard.rfp-timeline.add-milestone.label-date')}
              value={formData.date}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, date: e.target.value }));
                if (errors.date) setErrors((prev) => ({ ...prev, date: undefined }));
              }}
              disabled={isLoading}
              error={errors.date}
              className="w-full"
            />
          </div>

          <div>
            <TimePicker
              label={`${t('dashboard.rfp-timeline.add-milestone.label-time')} (${t('dashboard.rfp-timeline.add-milestone.optional')})`}
              value={formData.time}
              onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))}
              disabled={isLoading}
              variant={TimePickerVariant.Native}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <Button secondary rounded onClick={handleClose} disabled={isLoading} type="button">
            {t('dashboard.rfp-timeline.add-milestone.cancel')}
          </Button>
          <Button primary rounded onClick={handleSubmit} disabled={isLoading} type="button">
            {isLoading
              ? t('dashboard.rfp-timeline.add-milestone.saving')
              : t('dashboard.rfp-timeline.add-milestone.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};
